import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import PartnerContextHeader from '../../components/ui/PartnerContextHeader';

import MetricsCard from './components/MetricsCard';
import OpportunityCard from './components/OpportunityCard';
import ProjectCard from './components/ProjectCard';
import TenderCard from './components/TenderCard';
import OpportunitiesTable from './components/OpportunitiesTable';
import EngagementChart from './components/EngagementChart';
import FilterPanel from './components/FilterPanel';
import OpportunityDetailDrawer from './components/OpportunityDetailDrawer';
import ProjectStatusModal from './components/ProjectStatusModal';
import TenderSubmissionModal from './components/TenderSubmissionModal';
import EngagementPartnerSwitcher from './components/EngagementPartnerSwitcher';
import ProjectDetailDrawer from './components/ProjectDetailDrawer';
import TenderDetailDrawer from './components/TenderDetailDrawer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';
import { opportunitiesService, projectsService, tendersService, partnerService } from '../../services/partnerService';
import Pagination from '../../components/ui/Pagination';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';

// ─── Add Opportunity Modal ────────────────────────────────────────────────────
const AddOpportunityModal = ({ onClose, onSuccess, partners }) => {
  const [form, setForm] = useState({
    name: '',
    partner_id: '',
    value: '',
    opp_status: 'Identified',
    win_probability: '50',
    expected_close: '',
    sector: '',
    role: '',
    ecity_focal_member: '',
    partner_lead: '',
    description: '',
    owned_by: 'eCity',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Searchable partner dropdown state
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // eCity Focal Member state
  const [ecityMembers, setEcityMembers] = useState([]);
  const [ecitySearch, setEcitySearch] = useState('');
  const [ecityDropdownOpen, setEcityDropdownOpen] = useState(false);
  const [selectedEcityMember, setSelectedEcityMember] = useState(null);

  // Partner Lead state
  const [partnerContacts, setPartnerContacts] = useState([]);
  const [partnerLeadSearch, setPartnerLeadSearch] = useState('');
  const [partnerLeadDropdownOpen, setPartnerLeadDropdownOpen] = useState(false);
  const [selectedPartnerLead, setSelectedPartnerLead] = useState(null);

  // Fetch eCity members (user_profiles = internal eCity staff)
  useEffect(() => {
    const fetchEcityMembers = async () => {
      try {
        const { data } = await supabase?.from('user_profiles')?.select('id, full_name, email, role')?.eq('is_active', true)?.order('full_name');
        setEcityMembers(data || []);
      } catch (_) {}
    };
    fetchEcityMembers();
  }, []);

  // Fetch partner contacts when partner changes
  useEffect(() => {
    const fetchPartnerContacts = async () => {
      if (!form?.partner_id) { setPartnerContacts([]); return; }
      try {
        const { data } = await supabase?.from('partner_contacts')?.select('id, name, role, email')?.eq('partner_id', form?.partner_id)?.order('name');
        setPartnerContacts(data || []);
      } catch (_) {}
    };
    fetchPartnerContacts();
    // Reset partner lead when partner changes
    setSelectedPartnerLead(null);
    setForm(prev => ({ ...prev, partner_lead: '' }));
    setPartnerLeadSearch('');
  }, [form?.partner_id]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const filteredPartners = partners?.filter(p => {
    const name = (p?.brand_name || p?.legal_name || '')?.toLowerCase();
    return name?.includes(partnerSearch?.toLowerCase());
  });

  const filteredEcityMembers = ecityMembers?.filter(m =>
    (m?.full_name || m?.email || '')?.toLowerCase()?.includes(ecitySearch?.toLowerCase())
  );

  const filteredPartnerContacts = partnerContacts?.filter(c =>
    (c?.name || '')?.toLowerCase()?.includes(partnerLeadSearch?.toLowerCase())
  );

  const tierColors = {
    Gold: 'bg-yellow-100 text-yellow-700',
    Silver: 'bg-slate-100 text-slate-600',
    Platinum: 'bg-purple-100 text-purple-700',
    Bronze: 'bg-orange-100 text-orange-700',
  };

  const selectPartner = (p) => {
    setSelectedPartner(p);
    setForm(prev => ({ ...prev, partner_id: p?.id }));
    setPartnerDropdownOpen(false);
    setPartnerSearch('');
  };

  const clearPartner = () => {
    setSelectedPartner(null);
    setForm(prev => ({ ...prev, partner_id: '' }));
    setPartnerSearch('');
  };

  const selectEcityMember = (m) => {
    setSelectedEcityMember(m);
    setForm(prev => ({ ...prev, ecity_focal_member: m?.full_name || m?.email }));
    setEcityDropdownOpen(false);
    setEcitySearch('');
  };

  const clearEcityMember = () => {
    setSelectedEcityMember(null);
    setForm(prev => ({ ...prev, ecity_focal_member: '' }));
    setEcitySearch('');
  };

  const selectPartnerLead = (c) => {
    setSelectedPartnerLead(c);
    setForm(prev => ({ ...prev, partner_lead: c?.name }));
    setPartnerLeadDropdownOpen(false);
    setPartnerLeadSearch('');
  };

  const clearPartnerLead = () => {
    setSelectedPartnerLead(null);
    setForm(prev => ({ ...prev, partner_lead: '' }));
    setPartnerLeadSearch('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name?.trim()) { setError('Opportunity title is required.'); return; }
    if (form?.owned_by === 'eCity' && !form?.ecity_focal_member) { setError('eCity Focal Member is required when ownership is eCity.'); return; }
    if (form?.owned_by === 'Partner' && !form?.partner_lead) { setError('Partner Lead is required when ownership is Partner.'); return; }
    setSaving(true);
    setError(null);
    try {
      await opportunitiesService?.create({
        name: form?.name,
        partner_id: form?.partner_id || null,
        value: parseFloat(form?.value) || 0,
        opp_status: form?.opp_status,
        win_probability: parseInt(form?.win_probability) || 0,
        expected_close: form?.expected_close || null,
        sector: form?.sector,
        role: form?.role,
        description: form?.description,
        owned_by: form?.owned_by,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create opportunity.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-8">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100 bg-gradient-to-r from-secondary/5 to-primary/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
              <Icon name="Target" size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Add New Opportunity</h2>
              <p className="text-xs text-slate-500">Fill in the details below to create a new opportunity</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {error && (
            <div className="flex items-center gap-2 px-8 py-3 bg-red-50 border-b border-red-100 text-red-700 text-sm">
              <Icon name="AlertCircle" size={15} />
              {error}
            </div>
          )}

          <div className="p-8">
            {/* Owned By Toggle — full width row above title/partner */}
            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Owned By</label>
              <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50 p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, owned_by: 'eCity' }))}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    form?.owned_by === 'eCity' ?'bg-blue-600 text-white shadow-sm' :'text-slate-500 hover:text-slate-700 hover:bg-white'
                  }`}
                >
                  <span>🏢</span> eCity
                </button>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, owned_by: 'Partner' }))}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    form?.owned_by === 'Partner' ?'bg-purple-600 text-white shadow-sm' :'text-slate-500 hover:text-slate-700 hover:bg-white'
                  }`}
                >
                  <span>🤝</span> Partner
                </button>
              </div>
            </div>

            {/* Row 1: Title (60%) + Partner (40%) */}
            <div className="grid grid-cols-5 gap-5 mb-5">
              <div className="col-span-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Opportunity Title <span className="text-red-400">*</span></label>
                <input
                  name="name"
                  value={form?.name}
                  onChange={handleChange}
                  placeholder="e.g. Enterprise Cloud Migration"
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all placeholder-slate-300"
                  required
                />
              </div>

              {/* Partner searchable dropdown */}
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Partner</label>
                <div className="relative">
                  {selectedPartner ? (
                    <div className="flex items-center justify-between h-11 px-4 border border-slate-200 rounded-xl bg-white text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-slate-800 font-medium">{selectedPartner?.brand_name || selectedPartner?.legal_name}</span>
                        {selectedPartner?.tier && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${tierColors?.[selectedPartner?.tier] || 'bg-slate-100 text-slate-600'}`}>
                            {selectedPartner?.tier}
                          </span>
                        )}
                      </div>
                      <button type="button" onClick={clearPartner} className="ml-2 text-slate-400 hover:text-slate-600 flex-shrink-0">
                        <Icon name="X" size={13} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-2 h-11 px-4 border border-slate-200 rounded-xl cursor-pointer hover:border-primary/40 transition-colors"
                      onClick={() => setPartnerDropdownOpen(v => !v)}
                    >
                      <Icon name="Search" size={13} className="text-slate-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={partnerSearch}
                        onChange={e => { setPartnerSearch(e?.target?.value); setPartnerDropdownOpen(true); }}
                        onFocus={() => setPartnerDropdownOpen(true)}
                        placeholder="Search partner..."
                        className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-300"
                        onClick={e => e?.stopPropagation()}
                      />
                      <Icon name="ChevronDown" size={13} className={`text-slate-400 flex-shrink-0 transition-transform ${partnerDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  )}

                  {partnerDropdownOpen && !selectedPartner && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-44 overflow-y-auto">
                      {filteredPartners?.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-400">No partners found</div>
                      ) : (
                        <>
                          <div
                            className="px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 cursor-pointer border-b border-slate-100"
                            onClick={() => { clearPartner(); setPartnerDropdownOpen(false); }}
                          >
                            — No Partner —
                          </div>
                          {filteredPartners?.map(p => (
                            <div
                              key={p?.id}
                              className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-primary/5 cursor-pointer transition-colors"
                              onClick={() => selectPartner(p)}
                            >
                              <span className="text-slate-800 font-medium truncate">{p?.brand_name || p?.legal_name}</span>
                              {p?.tier && (
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${tierColors?.[p?.tier] || 'bg-slate-100 text-slate-600'}`}>
                                  {p?.tier}
                                </span>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Value (wider) | Stage | Win % (narrower) | Expected Close */}
            <div className="grid grid-cols-10 gap-5 mb-5">
              <div className="col-span-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Value (OMR)</label>
                <input
                  name="value"
                  type="number"
                  min="0"
                  value={form?.value}
                  onChange={handleChange}
                  placeholder="e.g. 250000"
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all placeholder-slate-300"
                />
              </div>

              <div className="col-span-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Stage</label>
                <select
                  name="opp_status"
                  value={form?.opp_status}
                  onChange={handleChange}
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 bg-white transition-all"
                >
                  {['Identified', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost']?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Win Probability (%)</label>
                <input
                  name="win_probability"
                  type="number"
                  min="0"
                  max="100"
                  value={form?.win_probability}
                  onChange={handleChange}
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Expected Close</label>
                <input
                  name="expected_close"
                  type="date"
                  value={form?.expected_close}
                  onChange={handleChange}
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all"
                />
              </div>
            </div>

            {/* Row 3: Sector (50%) | Engagement Role dropdown (50%) */}
            <div className="grid grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Sector</label>
                <select
                  name="sector"
                  value={form?.sector}
                  onChange={handleChange}
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 bg-white transition-all"
                >
                  <option value="">Select sector...</option>
                  {['Healthcare', 'Financial Services', 'Technology', 'Retail & E-Commerce', 'Manufacturing', 'Energy & Utilities', 'Telecommunications', 'Education', 'Government & Public Sector', 'Real Estate', 'Transportation & Logistics', 'Media & Entertainment', 'Professional Services', 'Non-Profit', 'Other']?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Engagement Role</label>
                <select
                  name="role"
                  value={form?.role}
                  onChange={handleChange}
                  className="w-full h-11 px-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 bg-white transition-all"
                >
                  <option value="">Select engagement role...</option>
                  {['Lead', 'Co-Lead', 'Support', 'Technical Advisor', 'Commercial', 'Delivery', 'Bid Manager']?.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            {/* Row 4: eCity Focal Member (50%) | Partner Lead (50%) */}
            <div className="grid grid-cols-2 gap-5 mb-5">
              {/* eCity Focal Member searchable dropdown */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  eCity Focal Member
                  <span className="ml-1 normal-case font-normal text-slate-400">(eCity side)</span>
                  {form?.owned_by === 'eCity' && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="relative">
                  {selectedEcityMember ? (
                    <div className="flex items-center justify-between h-11 px-4 border border-slate-200 rounded-xl bg-white text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-secondary/30 to-primary/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary">{(selectedEcityMember?.full_name || selectedEcityMember?.email || '?')?.[0]?.toUpperCase()}</span>
                        </div>
                        <span className="truncate text-slate-800 font-medium">{selectedEcityMember?.full_name || selectedEcityMember?.email}</span>
                        {selectedEcityMember?.role && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary flex-shrink-0 capitalize">{selectedEcityMember?.role}</span>
                        )}
                      </div>
                      <button type="button" onClick={clearEcityMember} className="ml-2 text-slate-400 hover:text-slate-600 flex-shrink-0">
                        <Icon name="X" size={13} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-2 h-11 px-4 border border-slate-200 rounded-xl cursor-pointer hover:border-primary/40 transition-colors"
                      onClick={() => setEcityDropdownOpen(v => !v)}
                    >
                      <Icon name="Search" size={13} className="text-slate-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={ecitySearch}
                        onChange={e => { setEcitySearch(e?.target?.value); setEcityDropdownOpen(true); }}
                        onFocus={() => setEcityDropdownOpen(true)}
                        placeholder="Search eCity member..."
                        className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-300"
                        onClick={e => e?.stopPropagation()}
                      />
                      <Icon name="ChevronDown" size={13} className={`text-slate-400 flex-shrink-0 transition-transform ${ecityDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  )}

                  {ecityDropdownOpen && !selectedEcityMember && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-44 overflow-y-auto">
                      {filteredEcityMembers?.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-400">No members found</div>
                      ) : (
                        <>
                          <div
                            className="px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 cursor-pointer border-b border-slate-100"
                            onClick={() => { clearEcityMember(); setEcityDropdownOpen(false); }}
                          >
                            — No Focal Member —
                          </div>
                          {filteredEcityMembers?.map(m => (
                            <div
                              key={m?.id}
                              className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-primary/5 cursor-pointer transition-colors"
                              onClick={() => selectEcityMember(m)}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-primary">{(m?.full_name || m?.email || '?')?.[0]?.toUpperCase()}</span>
                                </div>
                                <span className="text-slate-800 font-medium truncate">{m?.full_name || m?.email}</span>
                              </div>
                              {m?.role && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary flex-shrink-0 capitalize">{m?.role}</span>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Partner Lead searchable dropdown */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  Partner Lead
                  <span className="ml-1 normal-case font-normal text-slate-400">(partner side)</span>
                  {form?.owned_by === 'Partner' && <span className="text-red-400 ml-1">*</span>}
                </label>
                <div className="relative">
                  {selectedPartnerLead ? (
                    <div className="flex items-center justify-between h-11 px-4 border border-slate-200 rounded-xl bg-white text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-slate-600">{(selectedPartnerLead?.name || '?')?.[0]?.toUpperCase()}</span>
                        </div>
                        <span className="truncate text-slate-800 font-medium">{selectedPartnerLead?.name}</span>
                        {selectedPartnerLead?.role && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 flex-shrink-0">{selectedPartnerLead?.role}</span>
                        )}
                      </div>
                      <button type="button" onClick={clearPartnerLead} className="ml-2 text-slate-400 hover:text-slate-600 flex-shrink-0">
                        <Icon name="X" size={13} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`flex items-center gap-2 h-11 px-4 border border-slate-200 rounded-xl transition-colors ${!form?.partner_id ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:border-primary/40'}`}
                      onClick={() => form?.partner_id && setPartnerLeadDropdownOpen(v => !v)}
                    >
                      <Icon name="Search" size={13} className="text-slate-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={partnerLeadSearch}
                        onChange={e => { setPartnerLeadSearch(e?.target?.value); form?.partner_id && setPartnerLeadDropdownOpen(true); }}
                        onFocus={() => form?.partner_id && setPartnerLeadDropdownOpen(true)}
                        placeholder={form?.partner_id ? 'Search partner contact...' : 'Select a partner first'}
                        disabled={!form?.partner_id}
                        className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-300 disabled:cursor-not-allowed"
                        onClick={e => e?.stopPropagation()}
                      />
                      <Icon name="ChevronDown" size={13} className={`text-slate-400 flex-shrink-0 transition-transform ${partnerLeadDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  )}

                  {partnerLeadDropdownOpen && !selectedPartnerLead && form?.partner_id && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-44 overflow-y-auto">
                      {filteredPartnerContacts?.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-400">No contacts found for this partner</div>
                      ) : (
                        <>
                          <div
                            className="px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 cursor-pointer border-b border-slate-100"
                            onClick={() => { clearPartnerLead(); setPartnerLeadDropdownOpen(false); }}
                          >
                            — No Partner Lead —
                          </div>
                          {filteredPartnerContacts?.map(c => (
                            <div
                              key={c?.id}
                              className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-primary/5 cursor-pointer transition-colors"
                              onClick={() => selectPartnerLead(c)}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-slate-600">{(c?.name || '?')?.[0]?.toUpperCase()}</span>
                                </div>
                                <span className="text-slate-800 font-medium truncate">{c?.name}</span>
                              </div>
                              {c?.role && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 flex-shrink-0">{c?.role}</span>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 5: Description (full width) */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Description</label>
              <textarea
                name="description"
                value={form?.description}
                onChange={handleChange}
                rows={3}
                placeholder="Brief description of the opportunity..."
                className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 resize-none transition-all placeholder-slate-300"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-slate-100 bg-slate-50/60 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-secondary to-primary text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-60 flex items-center gap-2"
            >
              {saving
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
                : <><Icon name="Plus" size={15} /> Add Opportunity</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Active Filter Chips ──────────────────────────────────────────────────────
const FilterChips = ({ filters, onRemove, onClearAll }) => {
  const chips = [];

  if (filters?.sector && filters?.sector !== 'all') chips?.push({ key: 'sector', label: `Sector: ${filters?.sector}` });
  if (filters?.status && filters?.status !== 'all') chips?.push({ key: 'status', label: `Status: ${filters?.status}` });
  if (filters?.role && filters?.role !== 'all') chips?.push({ key: 'role', label: `Engagement Role: ${filters?.role}` });
  if (filters?.search) chips?.push({ key: 'search', label: `Search: "${filters?.search}"` });
  if (filters?.minValue) chips?.push({ key: 'minValue', label: `Min: OMR ${Number(filters?.minValue)?.toLocaleString()}` });
  if (filters?.maxValue) chips?.push({ key: 'maxValue', label: `Max: OMR ${Number(filters?.maxValue)?.toLocaleString()}` });
  if (filters?.fromDate) chips?.push({ key: 'fromDate', label: `From: ${filters?.fromDate}` });
  if (filters?.toDate) chips?.push({ key: 'toDate', label: `To: ${filters?.toDate}` });

  if (chips?.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-8 mb-16">
      <span className="text-xs font-semibold text-slate-500 flex items-center gap-6">
        <Icon name="Filter" size={12} />
        Active filters:
      </span>
      {chips?.map(chip => (
        <span
          key={chip?.key}
          className="inline-flex items-center gap-6 px-10 py-5 bg-primary/10 text-primary rounded-lg text-xs font-medium border border-primary/20"
        >
          {chip?.label}
          <button
            onClick={() => onRemove(chip?.key)}
            className="hover:text-primary/70 transition-colors"
          >
            <Icon name="X" size={11} />
          </button>
        </span>
      ))}
      <button
        onClick={onClearAll}
        className="text-xs text-slate-500 hover:text-slate-700 font-medium underline underline-offset-2 transition-colors"
      >
        Clear all
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EngagementManagementHub = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('opportunities');
  const [viewMode, setViewMode] = useState('cards');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddOppModal, setShowAddOppModal] = useState(false);
  const [partnersList, setPartnersList] = useState([]);

  // Partner switcher state
  const [activePartnerId, setActivePartnerId] = useState(null);
  const [activePartner, setActivePartner] = useState(null);

  // Drawer / Modal state
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTender, setSelectedTender] = useState(null);
  const [selectedProjectForDrawer, setSelectedProjectForDrawer] = useState(null);
  const [selectedTenderForDrawer, setSelectedTenderForDrawer] = useState(null);

  const [filters, setFilters] = useState({
    sector: 'all',
    status: 'all',
    role: 'all',
    search: '',
    minValue: '',
    maxValue: '',
    fromDate: '',
    toDate: ''
  });

  // Applied filters (only applied when user clicks Apply)
  const [appliedFilters, setAppliedFilters] = useState({
    sector: 'all',
    status: 'all',
    role: 'all',
    search: '',
    minValue: '',
    maxValue: '',
    fromDate: '',
    toDate: ''
  });

  const [opportunitiesData, setOpportunitiesData] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [tendersData, setTendersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chart data from Supabase
  const [engagementTrendData, setEngagementTrendData] = useState([]);
  const [winRateData, setWinRateData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  // Pagination state per tab
  const [oppPage, setOppPage] = useState(1);
  const [projPage, setProjPage] = useState(1);
  const [tenderPage, setTenderPage] = useState(1);
  const PAGE_SIZE = 9;

  useEffect(() => {
    loadPartners();
  }, []);

  useEffect(() => {
    loadData();
  }, [appliedFilters, activePartnerId]);

  useEffect(() => {
    loadChartData();
  }, [activePartnerId]);

  const loadPartners = async () => {
    try {
      const data = await partnerService?.getAll();
      setPartnersList(data || []);
    } catch (err) {
      console.error('Error loading partners:', err);
    }
  };

  const handleSelectPartner = (partnerId) => {
    setActivePartnerId(partnerId);
    if (partnerId) {
      const found = partnersList?.find(p => p?.id === partnerId);
      setActivePartner(found || null);
    } else {
      setActivePartner(null);
    }
    setOppPage(1);
    setProjPage(1);
    setTenderPage(1);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build filter params for opportunities
      const oppFilters = { ...appliedFilters };
      if (activePartnerId) oppFilters.partner_id = activePartnerId;

      const [opps, projects, tenders] = await Promise.all([
        opportunitiesService?.getAll(oppFilters),
        projectsService?.getAll(activePartnerId),
        tendersService?.getAll(activePartnerId)
      ]);

      setOpportunitiesData(opps?.map(o => ({
        id: o?.id,
        name: o?.name,
        sector: o?.sector,
        value: o?.value >= 1000000 ? `OMR ${(o?.value / 1000000)?.toFixed(1)}M` : `OMR ${(o?.value / 1000)?.toFixed(0)}K`,
        rawValue: o?.value,
        role: o?.role,
        status: o?.opp_status,
        winProbability: o?.win_probability,
        expectedClose: o?.expected_close ? new Date(o.expected_close)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
        rawExpectedClose: o?.expected_close || '',
        ownedBy: o?.owned_by || 'eCity',
        partner_id: o?.partner_id || '',
        partnerName: o?.partners?.brand_name || o?.partners?.legal_name || '',
        description: o?.description || '',
      })));

      setProjectsData(projects?.map(p => ({
        id: p?.id,
        projectId: `PRJ-${p?.id?.slice(0, 8)?.toUpperCase()}`,
        name: p?.name,
        deliveryRole: 'Joint Delivery',
        timeline: p?.start_date && p?.end_date ? `${new Date(p.start_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(p.end_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'TBD',
        value: p?.value >= 1000000 ? `OMR ${(p?.value / 1000000)?.toFixed(1)}M` : `OMR ${(p?.value / 1000)?.toFixed(0)}K`,
        rawValue: p?.value,
        status: p?.project_status,
        health: p?.health === 'on-track' ? 'On Track' : p?.health === 'at-risk' ? 'At Risk' : p?.health === 'delayed' ? 'Delayed' : 'On Track',
        rawHealth: p?.health || 'on-track',
        progress: p?.completion,
        partner_id: p?.partner_id || '',
        client: p?.client || '',
        rawStartDate: p?.start_date || '',
        rawEndDate: p?.end_date || '',
        description: p?.description || '',
      })));

      setTendersData(tenders?.map(t => ({
        id: t?.id,
        name: t?.name,
        client: t?.partners?.brand_name || 'TBD',
        value: t?.value >= 1000000 ? `OMR ${(t?.value / 1000000)?.toFixed(1)}M` : `OMR ${(t?.value / 1000)?.toFixed(0)}K`,
        rawValue: t?.value,
        status: t?.tender_status === 'open' ? 'Open' : t?.tender_status === 'submitted' ? 'Submitted' : t?.tender_status === 'under_evaluation' ? 'Under Evaluation' : 'Open',
        rawStatus: t?.tender_status || 'open',
        deadline: t?.deadline ? new Date(t.deadline)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
        rawDeadline: t?.deadline || '',
        daysLeft: t?.deadline ? Math.max(0, Math.ceil((new Date(t.deadline) - new Date()) / (1000 * 60 * 60 * 24))) : null,
        technicalRole: 'Lead',
        financialRole: 'Support',
        partner_id: t?.partner_id || '',
        sector: t?.sector || '',
        description: t?.description || '',
      })));
    } catch (err) {
      console.error('Error loading engagement data:', err);
      setError(err?.message);
      toast?.error('Failed to load engagement data.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Live Chart Data from Supabase ──────────────────────────────────────────
  const loadChartData = async () => {
    setChartsLoading(true);
    try {
      // Engagement trend: count opportunities per month (last 6 months)
      let oppQuery = supabase
        ?.from('opportunities')
        ?.select('created_at, value')
        ?.gte('created_at', new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)?.toISOString())
        ?.order('created_at', { ascending: true });

      if (activePartnerId) oppQuery = oppQuery?.eq('partner_id', activePartnerId);

      const { data: oppData } = await oppQuery;

      // Group by month
      const monthMap = {};
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const now = new Date();

      // Build last 6 months skeleton
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${monthNames?.[d?.getMonth()]} ${d?.getFullYear()}`;
        monthMap[key] = { name: monthNames?.[d?.getMonth()], value: 0, target: Math.floor(10 + i * 2) };
      }

      oppData?.forEach(o => {
        const d = new Date(o?.created_at);
        const key = `${monthNames?.[d?.getMonth()]} ${d?.getFullYear()}`;
        if (monthMap?.[key]) monthMap[key].value += 1;
      });

      setEngagementTrendData(Object.values(monthMap));

      // Win rate: won vs total per quarter (last 5 quarters)
      let allOppsQuery = supabase
        ?.from('opportunities')
        ?.select('created_at, opp_status')
        ?.gte('created_at', new Date(Date.now() - 450 * 24 * 60 * 60 * 1000)?.toISOString());

      if (activePartnerId) allOppsQuery = allOppsQuery?.eq('partner_id', activePartnerId);

      const { data: allOpps } = await allOppsQuery;

      const quarterMap = {};
      const getQuarter = (date) => {
        const d = new Date(date);
        const q = Math.floor(d?.getMonth() / 3) + 1;
        return `Q${q} ${d?.getFullYear()}`;
      };

      allOpps?.forEach(o => {
        const q = getQuarter(o?.created_at);
        if (!quarterMap?.[q]) quarterMap[q] = { name: q, total: 0, won: 0 };
        quarterMap[q].total += 1;
        if (o?.opp_status === 'Won') quarterMap[q].won += 1;
      });

      const winRateArr = Object.values(quarterMap)
        ?.slice(-5)
        ?.map(q => ({
          name: q?.name,
          value: q?.total > 0 ? Math.round((q?.won / q?.total) * 100) : 0,
          target: 70
        }));

      setWinRateData(winRateArr?.length > 0 ? winRateArr : [
        { name: 'Q1 2025', value: 0, target: 70 },
        { name: 'Q2 2025', value: 0, target: 70 },
      ]);
    } catch (err) {
      console.error('Chart data error:', err);
      // Fallback to empty arrays — charts will show no data gracefully
      setEngagementTrendData([]);
      setWinRateData([]);
    } finally {
      setChartsLoading(false);
    }
  };

  // Paginated slices
  const paginatedOpps = opportunitiesData?.slice((oppPage - 1) * PAGE_SIZE, oppPage * PAGE_SIZE);
  const paginatedProjs = projectsData?.slice((projPage - 1) * PAGE_SIZE, projPage * PAGE_SIZE);
  const paginatedTenders = tendersData?.slice((tenderPage - 1) * PAGE_SIZE, tenderPage * PAGE_SIZE);

  const metricsData = [
    {
      icon: 'DollarSign',
      label: 'Total Pipeline Value',
      value: opportunitiesData?.length > 0 ? `OMR ${(opportunitiesData?.reduce((sum, o) => {
        const num = parseFloat(o?.value?.replace(/[OMR\sMK]/g, '') || 0);
        return sum + (o?.value?.includes('M') ? num * 1000000 : num * 1000);
      }, 0) / 1000000)?.toFixed(1)}M` : 'OMR 0',
      subValue: `Across ${opportunitiesData?.length} opportunities`,
      trend: '+18%',
      trendDirection: 'up',
      iconColor: 'text-success'
    },
    {
      icon: 'TrendingUp',
      label: 'Win Rate',
      value: '68%',
      subValue: 'Last 6 months',
      trend: '+5%',
      trendDirection: 'up',
      iconColor: 'text-primary'
    },
    {
      icon: 'FolderOpen',
      label: 'Active Projects',
      value: String(projectsData?.length),
      subValue: `${projectsData?.filter(p => p?.health === 'On Track')?.length} on track, ${projectsData?.filter(p => p?.health === 'At Risk')?.length} at risk`,
      trend: '+3',
      trendDirection: 'up',
      iconColor: 'text-accent'
    },
    {
      icon: 'FileText',
      label: 'Open Tenders',
      value: String(tendersData?.filter(t => t?.status === 'Open')?.length),
      subValue: `${tendersData?.filter(t => t?.status === 'Submitted')?.length} submissions pending`,
      trend: '-2',
      trendDirection: 'down',
      iconColor: 'text-warning'
    }
  ];

  const tabs = [
    { id: 'opportunities', label: 'Joint Opportunities', icon: 'Target', count: opportunitiesData?.length },
    { id: 'projects', label: 'Active Projects', icon: 'FolderOpen', count: projectsData?.length },
    { id: 'tenders', label: 'RFQs & Tenders', icon: 'FileText', count: tendersData?.length }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setOppPage(1);
  };

  const handleResetFilters = () => {
    const empty = { sector: 'all', status: 'all', role: 'all', search: '', minValue: '', maxValue: '', fromDate: '', toDate: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setOppPage(1);
  };

  const handleRemoveChip = (key) => {
    const defaultVal = (key === 'sector' || key === 'status' || key === 'role') ? 'all' : '';
    const updated = { ...appliedFilters, [key]: defaultVal };
    setAppliedFilters(updated);
    setFilters(updated);
    setOppPage(1);
  };

  const handleSavePreset = () => console.log('Save filter preset:', filters);
  const handleViewDetails = (item) => setSelectedOpportunity(item);
  const handleUpdateStatus = (item) => setSelectedProject(item);
  const handleTenderDetails = (item) => setSelectedTender(item);
  const handleViewProjectDetails = (item) => setSelectedProjectForDrawer(item);
  const handleViewTenderDetails = (item) => setSelectedTenderForDrawer(item);
  const handleSort = (field, direction) => console.log('Sort by:', field, direction);

  // Edit handlers — open detail drawers in edit mode
  const handleEditOpportunity = (item) => setSelectedOpportunity(item);
  const handleEditProject = (item) => setSelectedProjectForDrawer(item);
  const handleEditTender = (item) => setSelectedTenderForDrawer(item);

  // Delete handlers — delete directly with confirmation already handled in card
  const handleDeleteOpportunity = async (item) => {
    try {
      const { error } = await supabase?.from('opportunities')?.delete()?.eq('id', item?.id);
      if (error) throw error;
      toast?.success('Opportunity deleted');
      loadData();
      loadChartData();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete opportunity');
    }
  };

  const handleDeleteProject = async (item) => {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', item?.id);
      if (error) throw error;
      toast?.success('Project deleted');
      loadData();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete project');
    }
  };

  const handleDeleteTender = async (item) => {
    try {
      const { error } = await supabase?.from('tenders')?.delete()?.eq('id', item?.id);
      if (error) throw error;
      toast?.success('Tender deleted');
      loadData();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete tender');
    }
  };

  // Check if any filter is active
  const hasActiveFilters = appliedFilters?.sector !== 'all' || appliedFilters?.status !== 'all' ||
    appliedFilters?.role !== 'all' || appliedFilters?.search || appliedFilters?.minValue ||
    appliedFilters?.maxValue || appliedFilters?.fromDate || appliedFilters?.toDate;

  return (
    <>
      <Helmet>
        <title>Engagement Management Hub - PartnerHub</title>
        <meta name="description" content="Track opportunities, manage projects, and monitor tenders through comprehensive engagement lifecycle management" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <PartnerContextHeader />

        {/* Partner Switcher Bar */}
        <div className="pt-4">
          <EngagementPartnerSwitcher
            partners={partnersList}
            selectedPartnerId={activePartnerId}
            selectedPartner={activePartner}
            onSelectPartner={handleSelectPartner}
            loading={loading && partnersList?.length === 0}
          />
        </div>

        <main className="pb-32 px-24 lg:px-32 pt-8">
          <div className="max-w-[1600px] mx-auto">
            {/* Page Header */}
            <div className="mb-32">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20 mb-24">
                <div>
                  <div className="flex items-center gap-12 mb-12">
                    <div className="p-12 bg-gradient-to-br from-secondary to-primary rounded-xl shadow-lg">
                      <Icon name="Users" size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                        Engagement Management
                      </h1>
                      <p className="text-slate-600 mt-4">
                        Pipeline tracking and project oversight
                        {activePartner && (
                          <span className="ml-8 text-primary font-semibold">
                            — {activePartner?.brand_name || activePartner?.legal_name}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-12">
                  <Button
                    variant="outline"
                    size="icon"
                    iconName="Filter"
                    iconSize={20}
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? 'bg-primary text-white' : ''}
                  />
                  <div className="flex items-center gap-4 bg-white rounded-xl p-4 border border-slate-200">
                    <button
                      onClick={() => setViewMode('cards')}
                      className={`p-8 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                      <Icon name="LayoutGrid" size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-8 rounded-lg transition-all ${viewMode === 'table' ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                      <Icon name="List" size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 mb-24">
                {metricsData?.map((metric, index) => (
                  <MetricsCard key={index} {...metric} />
                ))}
              </div>

              {/* Tabs */}
              <div className="flex gap-8 overflow-x-auto pb-4 scrollbar-hide">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-10 px-24 py-14 rounded-xl font-semibold text-sm whitespace-nowrap transition-all
                      ${activeTab === tab?.id
                        ? 'bg-gradient-to-r from-secondary to-primary text-white shadow-lg shadow-secondary/25'
                        : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={18} />
                    {tab?.label}
                    <span className={`px-8 py-2 rounded-full text-xs font-bold ${activeTab === tab?.id ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>
                      {tab?.count}
                    </span>
                  </button>
                ))}

                <button
                  onClick={() => setShowAddOppModal(true)}
                  className="ml-auto flex items-center gap-8 px-20 py-14 rounded-xl bg-gradient-to-r from-secondary to-primary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                >
                  <Icon name="Plus" size={16} />
                  Add Opportunity
                </button>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-64">
                <div className="flex flex-col items-center gap-16">
                  <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                  <p className="text-slate-500 text-sm">Loading engagement data...</p>
                </div>
              </div>
            )}

            {error && !loading && (
              <div className="flex items-center gap-12 p-20 bg-red-50 border border-red-200 rounded-xl mb-24 text-red-700 text-sm">
                <Icon name="AlertCircle" size={18} />
                <span>Error loading data: {error}</span>
                <button onClick={loadData} className="ml-auto text-red-600 hover:text-red-800 font-medium">Retry</button>
              </div>
            )}

            {showFilters && (
              <div className="mb-16">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters}
                  onSavePreset={handleSavePreset}
                  onApply={handleApplyFilters}
                />
              </div>
            )}

            {/* Active Filter Chips */}
            {hasActiveFilters && (
              <FilterChips
                filters={appliedFilters}
                onRemove={handleRemoveChip}
                onClearAll={handleResetFilters}
              />
            )}

            {!loading && activeTab === 'opportunities' && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {viewMode === 'cards' ? (
                  <div className="p-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                    {paginatedOpps?.length > 0 ? paginatedOpps?.map((opportunity) => (
                      <OpportunityCard key={opportunity?.id} opportunity={opportunity} onViewDetails={handleViewDetails} onEdit={handleEditOpportunity} onDelete={handleDeleteOpportunity} />
                    )) : (
                      <div className="col-span-3 flex flex-col items-center justify-center py-48 text-slate-400 text-sm gap-12">
                        <Icon name="Target" size={36} className="opacity-30" />
                        <p>No opportunities found. Try adjusting your filters or add a new opportunity.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <OpportunitiesTable opportunities={paginatedOpps} onViewDetails={handleViewDetails} onSort={handleSort} />
                )}
                <Pagination
                  currentPage={oppPage}
                  totalPages={Math.ceil(opportunitiesData?.length / PAGE_SIZE)}
                  totalItems={opportunitiesData?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setOppPage}
                />
              </div>
            )}

            {!loading && activeTab === 'projects' && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="p-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                  {paginatedProjs?.length > 0 ? paginatedProjs?.map((project) => (
                    <ProjectCard key={project?.id} project={project} onUpdateStatus={handleUpdateStatus} onViewDetails={handleViewProjectDetails} onEdit={handleEditProject} onDelete={handleDeleteProject} />
                  )) : (
                    <div className="col-span-3 flex items-center justify-center py-48 text-slate-400 text-sm">No projects found</div>
                  )}
                </div>
                <Pagination
                  currentPage={projPage}
                  totalPages={Math.ceil(projectsData?.length / PAGE_SIZE)}
                  totalItems={projectsData?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setProjPage}
                />
              </div>
            )}

            {!loading && activeTab === 'tenders' && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="p-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                  {paginatedTenders?.length > 0 ? paginatedTenders?.map((tender) => (
                    <TenderCard key={tender?.id} tender={tender} onViewDetails={handleViewTenderDetails} onEdit={handleEditTender} onDelete={handleDeleteTender} />
                  )) : (
                    <div className="col-span-3 flex items-center justify-center py-48 text-slate-400 text-sm">No tenders found</div>
                  )}
                </div>
                <Pagination
                  currentPage={tenderPage}
                  totalPages={Math.ceil(tendersData?.length / PAGE_SIZE)}
                  totalItems={tendersData?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setTenderPage}
                />
              </div>
            )}
          </div>

          {/* Charts — live from Supabase */}
          <div className="max-w-[1600px] mx-auto mt-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-20 lg:gap-24">
              {chartsLoading ? (
                <>
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm h-64 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-10 text-slate-400">
                      <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
                      <span className="text-xs">Loading chart data...</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm h-64 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-10 text-slate-400">
                      <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
                      <span className="text-xs">Loading chart data...</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <EngagementChart
                    type="line"
                    data={engagementTrendData}
                    title={`Engagement Trend — Last 6 Months${activePartner ? ` (${activePartner?.brand_name || activePartner?.legal_name})` : ''}`}
                  />
                  <EngagementChart
                    type="bar"
                    data={winRateData}
                    title={`Win Rate Performance${activePartner ? ` (${activePartner?.brand_name || activePartner?.legal_name})` : ''}`}
                  />
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Add Opportunity Modal */}
      {showAddOppModal && (
        <AddOpportunityModal
          onClose={() => setShowAddOppModal(false)}
          onSuccess={() => {
            setShowAddOppModal(false);
            loadData();
            loadChartData();
            toast?.success('Opportunity added successfully!');
          }}
          partners={partnersList}
        />
      )}

      {/* Opportunity Detail Drawer */}
      {selectedOpportunity && (
        <OpportunityDetailDrawer
          opportunity={selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
          onUpdated={() => { loadData(); loadChartData(); setSelectedOpportunity(null); }}
          partners={partnersList}
        />
      )}

      {/* Project Status Modal */}
      {selectedProject && (
        <ProjectStatusModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onUpdated={loadData}
        />
      )}

      {/* Project Detail Drawer */}
      {selectedProjectForDrawer && (
        <ProjectDetailDrawer
          project={selectedProjectForDrawer}
          onClose={() => setSelectedProjectForDrawer(null)}
          onUpdated={() => { loadData(); setSelectedProjectForDrawer(null); }}
          partners={partnersList}
        />
      )}

      {/* Tender Submission Modal */}
      {selectedTender && (
        <TenderSubmissionModal
          tender={selectedTender}
          onClose={() => setSelectedTender(null)}
          onUpdated={loadData}
        />
      )}

      {/* Tender Detail Drawer */}
      {selectedTenderForDrawer && (
        <TenderDetailDrawer
          tender={selectedTenderForDrawer}
          onClose={() => setSelectedTenderForDrawer(null)}
          onUpdated={() => { loadData(); setSelectedTenderForDrawer(null); }}
          partners={partnersList}
        />
      )}
    </>
  );
};

export default EngagementManagementHub;